import { db } from "@/services/db";
import { SAMPLE_ACCOUNT_ID } from "@/lib/constants";

/**
 * Completely clears all user data for security purposes
 * This should be called when:
 * 1. User signs out
 * 2. Shared computer usage detected
 * 3. Manual security clear requested
 */
export async function clearAllUserDataForSecurity(): Promise<void> {
  console.log("🔒 [Security] Clearing all user data...");

  try {
    // 1. Clear all account data from IndexedDB (except sample data)
    const allAccounts = await db.accounts.toArray();
    const userAccounts = allAccounts.filter((acc) => !acc.isSampleAccount);

    await Promise.all(
      userAccounts.map((acc) => db.deleteAccountData(acc.dexieId)),
    );

    // 2. Clear localStorage (but preserve some essential app settings)
    const preserveKeys = ["theme", "sidebar-state"]; // Add keys to preserve
    const storage: Record<string, string> = {};

    preserveKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) storage[key] = value;
    });

    localStorage.clear();

    // Restore preserved settings
    Object.entries(storage).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });

    // 3. Clear sessionStorage completely
    sessionStorage.clear();

    // 4. Clear browser cache for user data
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const requests = await cache.keys();
        for (const request of requests) {
          // Clear any cached user-specific data
          if (
            request.url.includes("google") ||
            request.url.includes("sheets") ||
            request.url.includes("account-data")
          ) {
            await cache.delete(request);
          }
        }
      }
    }

    console.log("✅ [Security] All user data cleared successfully");
  } catch (error) {
    console.error("❌ [Security] Failed to clear user data:", error);
    throw error;
  }
}

/**
 * Quick security check - returns true if user data might still be visible
 */
export function hasUserDataInMemory(accountData: Record<string, any>): boolean {
  const userDataKeys = Object.keys(accountData).filter(
    (key) => key !== SAMPLE_ACCOUNT_ID,
  );
  return userDataKeys.length > 0;
}

/**
 * Emergency clear function that can be called from console
 * Usage: window.emergencyClear()
 */
export function setupEmergencyClear(): void {
  if (typeof window !== "undefined") {
    (window as any).emergencyClear = async () => {
      try {
        await clearAllUserDataForSecurity();
        console.log("🔄 Reloading page...");
        window.location.reload();
      } catch (error) {
        console.error("Emergency clear failed:", error);
        console.log("🔄 Force reloading...");
        window.location.reload();
      }
    };

    console.log(
      "🚨 Emergency clear function available: window.emergencyClear()",
    );
  }
}
